import { CircleHelp, Play } from "lucide-react";
import { useTranslation } from "react-i18next";

import { TOUR_HELP_TARGET } from "../constants";
import { getTourSections } from "../tourSteps";
import { useTour } from "../useTour";
import { useTourDuration } from "../useTourDuration";
import { useTourState } from "../useTourState";
import { useTourTarget } from "../useTourTarget";
import { TourBeacon } from "./TourBeacon";

import { Button } from "@/components/ui/v2/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/v2/dropdown-menu";

export function TourHelpMenu() {
  const { t } = useTranslation();
  const { definition, stepIds, start, isRunning } = useTour();
  const duration = useTourDuration();
  const { showHelpBeacon } = useTourState(definition);
  const helpRef = useTourTarget<HTMLButtonElement>(TOUR_HELP_TARGET);
  const key = (part: string) => `${definition.copyKey}.help.${part}`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button ref={helpRef} variant="outline" className="relative">
          <CircleHelp />
          {t(key("button"))}
          {showHelpBeacon && (
            <>
              <TourBeacon className="absolute -right-1 -top-1" />
              <span className="sr-only">{t(`${definition.copyKey}.menuBeacon.description`)}</span>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-80"
        onCloseAutoFocus={(event) => isRunning && event.preventDefault()}
      >
        <DropdownMenuItem className="gap-2.5 p-2.5" onSelect={() => start()}>
          <Play aria-hidden className="size-5" />
          <div className="flex flex-col gap-px">
            <span className="text-sm font-semibold">{t(key("tour.title"))}</span>
            <span className="text-xs text-muted-foreground">
              {t(key("tour.meta"), { count: stepIds.length, duration })}
            </span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuLabel>{t(key("jumpTo"))}</DropdownMenuLabel>
        {getTourSections(definition, stepIds).map(({ section, fromCanonical, stepCount }) => (
          <DropdownMenuItem key={section} className="gap-2.5 px-2.5" onSelect={() => start(fromCanonical)}>
            <span className="flex-1">{t(key(`section.${section}`))}</span>
            <span className="text-xs text-muted-foreground">
              {t(key("sectionMeta"), { count: stepCount })}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
